import React, { useState } from 'react';

type AlertColor = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';

const COLOR_STYLES: Record<string, string> = {
  primary: 'bg-blue-500/10 border-blue-500/30 text-blue-700 dark:text-blue-300',
  secondary: 'bg-zinc-500/10 border-zinc-500/30 text-zinc-700 dark:text-zinc-300',
  success: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300',
  danger: 'bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-300',
  warning: 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300',
  info: 'bg-sky-500/10 border-sky-500/30 text-sky-700 dark:text-sky-300',
  light: 'bg-zinc-100 border-zinc-200 text-zinc-800',
  dark: 'bg-zinc-800 border-zinc-700 text-zinc-100',
};

type ErrorUIProps = {
  error: Error & { code?: string; data?: any };
};

/**
 * A UI component that displays error information in a user-friendly format.
 */
export function ErrorUI({ error }: ErrorUIProps) {
  if (error?.code === 'AKC-VAL-001') {
    return (
      <div>
        <h4 className="text-base font-semibold mb-2">Validation Error</h4>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          {Object.entries(error.data ?? {}).map(([k, v]) => (
            <li key={k}>
              <b className="font-medium">{k}</b>: {v as any}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return <h5 className="text-sm font-medium">{error?.message || `${error}`}</h5>;
}


type NotificationProps = {
  children?: React.ReactNode;
  error?: Error;
  color?: AlertColor | string;
  className?: string;
  onClose?: () => void;
  isOpen?: boolean;
  [key: string]: any;
};

/**
 * A notification alert component that optionally displays an error.
 */
function Notification(props: NotificationProps) {
  const { error, className = '', onClose, isOpen, ...rest } = props;
  let { children, color } = props;
  const [show, setShow] = useState(true);

  if (error) {
    children = <ErrorUI error={error as any} />;
    color = color ?? 'danger';
  }

  const open = isOpen ?? show;
  if (!open) return null;

  const colorClass = COLOR_STYLES[color ?? 'info'] ?? COLOR_STYLES.info;

  const handleClose = () => {
    onClose?.();
    setShow(prev => !prev);
  };

  // Strip props that shouldn't pass through (they were already consumed via `rest`)
  delete (rest as any).children;
  delete (rest as any).color;

  return (
    <div
      role="alert"
      className={`relative flex items-start gap-3 px-4 py-3 mb-3 rounded-xl border ${colorClass} ${className}`}
      {...rest}
    >
      <div className="flex-1 text-sm">{children}</div>
      {onClose && (
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="shrink-0 text-current opacity-60 hover:opacity-100 transition-opacity text-lg leading-none"
        >
          ×
        </button>
      )}
    </div>
  );
}

export default Notification;
